// The Director speaks through ElevenLabs v3, which reads audio tags like "[excited]" or "[laughs softly]" as
// performance cues. They must never show in the text chat (spec-v2-video.md, W4 "Transcript scrub"). Only the
// Director's lines go through this; what the user types is shown exactly as typed.

/** Words that mark a bracketed or starred aside as a stage direction, not content. */
const CUE_WORDS =
  "excited|excitedly|exciting|laugh|laughs|laughing|laughter|chuckle|chuckles|chuckling|giggle|giggles|sigh|sighs|sighing|" +
  "whisper|whispers|whispering|softly|gently|warmly|cheerfully|happily|sadly|calmly|quietly|brightly|playfully|eagerly|" +
  "enthusiastic|enthusiastically|pause|pauses|beat|smile|smiles|smiling|grin|grins|grinning|gasp|gasps|clears throat|" +
  "sarcastic|sarcastically|serious|seriously|playful|gentle|upbeat|energetic|surprised|curious|curiously|thoughtful|" +
  "thoughtfully|warm|calm|cheerful|happy|sad|nervous|nervously|amused|impressed|reassuring|reassuringly|confident|" +
  "confidently|friendly|delighted|proud|proudly|relieved|hesitant|hesitates|inhales|exhales|breath|breathes|" +
  "short pause|long pause|sings|singing|shouts|shouting|mischievously|dramatic|dramatically|tone";

const CUE = new RegExp(`\\b(${CUE_WORDS})\\b`, "i");

/**
 * Strip audio tags and emotion markers from a Director message:
 *   - every `[...]` tag (v3 audio tags are always bracketed; the Director never needs brackets in prose)
 *   - `*laughs*` / `(excitedly)` style asides when they read like a stage direction
 *   - SSML-ish tags such as `<break time="1s"/>`
 *   - a half-streamed tag left dangling at the end ("Great [exci")
 * and tidy the spacing left behind.
 */
export function scrubDirectorText(text: string): string {
  let t = text;
  t = t.replace(/\[[^[\]\n]{0,80}\]/g, " ");
  t = t.replace(/<\/?[a-z][a-z0-9_-]*(?:\s[^<>]*)?\/?>/gi, " ");
  t = t.replace(/\*{1,2}([^*\n]{1,40})\*{1,2}/g, (m, inner: string) => (CUE.test(inner) && inner.trim().split(/\s+/).length <= 4 ? " " : m));
  t = t.replace(/\(([^()\n]{1,40})\)/g, (m, inner: string) => (CUE.test(inner) && inner.trim().split(/\s+/).length <= 3 ? " " : m));
  t = t.replace(/\[[^\]\n]{0,30}$/, " ");
  // tidy: collapse runs of spaces, no space before punctuation, no leading punctuation left by a removed tag
  t = t.replace(/[ \t]{2,}/g, " ");
  t = t.replace(/ +([,.!?;:])/g, "$1");
  t = t.replace(/^[\s,;:]+/, "");
  t = t.replace(/\n[ \t]+/g, "\n").replace(/[ \t]+\n/g, "\n");
  return t.trim();
}

/**
 * v4 (V4-K1): the app's own "[app] …" user messages (finding F3: the end of a non-blocking play, stage picks) go to
 * the Director as user input, and the platform echoes them back as user lines: never show those in the transcript.
 */
export const isAppLine = (text: string) => /^\s*\[app\]/i.test(text);
